import React from 'react';
import { Box, Typography, Link as MuiLink, Paper, useTheme } from '@mui/material';
import { motion } from 'framer-motion';

const variants = {
  main: {
    component: Paper,
    sx: theme => ({
      p: { xs: 4, sm: 7, md: 10 },
      px: { xs: 3, sm: 7, md: 12 },
      mb: 4,
      bgcolor: 'background.paper',
      borderRadius: 4,
      boxShadow: 3,
      width: '100%',
      maxWidth: 900,
      mx: 'auto',
    })
  },
  sub: {
    component: Box,
    sx: theme => ({
      p: { xs: 1, md: 2 },
      mb: 2,
      bgcolor: theme.palette.grey[50],
      borderRadius: 2,
      width: '100%',
      maxWidth: 800,
      mx: 'auto',
    })
  },
  footer: {
    component: Box,
    sx: theme => ({
      p: 2,
      mt: 4,
      bgcolor: 'background.paper',
      color: theme.palette.text.secondary,
      textAlign: 'center',
      fontSize: '0.95rem',
      borderRadius: 4,
      width: '100%',
      maxWidth: 700,
      mx: 'auto',
      boxShadow: 1,
    })
  },
  link: {
    component: MuiLink,
    sx: theme => ({
      color: theme.palette.primary.main,
      fontWeight: 600,
      textDecoration: 'underline',
      cursor: 'pointer',
      mx: 0.5,
    })
  }
};

const SectionContent = ({
  children,
  variant = 'main',
  spacing = 4,
  align = 'left',
  as = 'section',
  heading,
  headingLevel = 2,
  link,
  fileLink,
  seo = false,
  ...props
}) => {
  const theme = useTheme();
  const { component: Component, sx } = variants[variant] || variants.main;
  const motionProps = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };
  const Tag = seo ? `h${headingLevel}` : undefined;

  // Only pass allowed props to MUI components
  const { theme: _theme, ...muiProps } = props;

  return (
    <Component
      as={as}
      sx={{ ...sx(theme), mb: spacing, textAlign: align, ...props.sx }}
      {...(variant === 'link' ? { href: fileLink || link, target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...muiProps}
      component={motion.div}
      {...motionProps}
    >
      {heading && (
        <Typography
          variant={seo ? `h${headingLevel}` : 'h5'}
          component={Tag || 'div'}
          sx={{ fontWeight: 700, mb: 2, color: theme.palette.primary.main }}
        >
          {heading}
        </Typography>
      )}
      {children}
    </Component>
  );
};

export default SectionContent;
